import { ChevronDown, ChevronUp, Check } from "@tamagui/lucide-icons";
import { forwardRef, ReactNode } from "react";
import {
  Adapt,
  Sheet,
  styled,
  Select as TamaguiSelect,
  SelectProps as TamaguiSelectProps,
  YStack,
  Text,
} from "tamagui";
import { LinearGradient } from "tamagui/linear-gradient";

export type SelectOption = {
  label: string;
  value: string;
};

export interface SelectProps extends TamaguiSelectProps {
  options?: SelectOption[];
  placeholder?: ReactNode;
  trigger?: ReactNode;
  special?: boolean;
  chromeless?: boolean;
  error?: any;
  helperText?: ReactNode;
  onClose?: () => void;
}

const StyledTrigger = styled(TamaguiSelect.Trigger, {
  name: "StyledSelectTrigger",
  backgroundColor: "$gray1",
  borderColor: "$gray6",
  height: "$4",
  variants: {
    special: {
      true: {
        backgroundColor: "$gray2",
        borderColor: "$white10",
        height: "$4.5",
        fontSize: 18,
      },
    },
    chromeless: {
      true: {
        backgroundColor: "transparent",
        borderColor: "transparent",
        shadowColor: "transparent",
        paddingVertical: 0,
        borderWidth: 0,
        pressStyle: {
          backgroundColor: "#FFFFFF",
          borderColor: "transparent",
        },
      },
    },
  },
});

export const Select = forwardRef<any, SelectProps>(
  (
    {
      options,
      placeholder,
      trigger,
      special = false,
      chromeless = false,
      error,
      helperText,
      ...props
    },
    ref,
  ) => (
    <YStack>
      <TamaguiSelect disablePreventBodyScroll {...props}>
        {trigger || (
          <StyledTrigger
            ref={ref}
            // No shrink: a long value in a narrow select ellipsizes instead.
            iconAfter={<ChevronDown size={20} flexShrink={0} />}
            special={special}
            chromeless={chromeless}
            borderColor={error ? "$red10" : undefined}
          >
            <TamaguiSelect.Value placeholder={placeholder} />
          </StyledTrigger>
        )}

        <Adapt when="maxMd" platform="touch">
          <Sheet
            native={!!props.native}
            modal
            dismissOnSnapToBottom
            animation="medium"
          >
            <Sheet.Frame>
              <Sheet.ScrollView>
                <Adapt.Contents />
              </Sheet.ScrollView>
            </Sheet.Frame>
            <Sheet.Overlay
              backgroundColor="$shadowColor"
              animation="lazy"
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
            />
          </Sheet>
        </Adapt>

        <TamaguiSelect.Content zIndex={200000}>
          <TamaguiSelect.ScrollUpButton
            alignItems="center"
            justifyContent="center"
            position="relative"
            width="100%"
            height="$3"
          >
            <YStack zIndex={10}>
              <ChevronUp size={20} />
            </YStack>
            <LinearGradient
              start={[0, 0]}
              end={[0, 1]}
              fullscreen
              colors={["$background", "transparent"]}
              borderRadius="$4"
            />
          </TamaguiSelect.ScrollUpButton>

          <TamaguiSelect.Viewport minWidth={200}>
            <TamaguiSelect.Group>
              {options?.map(({ label, value }, index) => (
                <TamaguiSelect.Item index={index} key={label} value={value}>
                  <TamaguiSelect.ItemText>{label}</TamaguiSelect.ItemText>
                  <TamaguiSelect.ItemIndicator marginLeft="auto">
                    <Check size={16} />
                  </TamaguiSelect.ItemIndicator>
                </TamaguiSelect.Item>
              ))}
            </TamaguiSelect.Group>

            {props.native && (
              <YStack
                position="absolute"
                right={0}
                top={0}
                bottom={0}
                alignItems="center"
                justifyContent="center"
                width={"$4"}
                pointerEvents="none"
              >
                <ChevronDown size={20} />
              </YStack>
            )}
          </TamaguiSelect.Viewport>

          <TamaguiSelect.ScrollDownButton
            alignItems="center"
            justifyContent="center"
            position="relative"
            width="100%"
            height="$3"
          >
            <YStack zIndex={10}>
              <ChevronDown size={20} />
            </YStack>
            <LinearGradient
              start={[0, 0]}
              end={[0, 1]}
              fullscreen
              colors={["transparent", "$background"]}
              borderRadius="$4"
            />
          </TamaguiSelect.ScrollDownButton>
        </TamaguiSelect.Content>
      </TamaguiSelect>
      {helperText && (
        <YStack marginTop={4}>
          <Text color={error ? "$red10" : "$gray10"} fontSize={13}>
            {helperText}
          </Text>
        </YStack>
      )}
    </YStack>
  ),
);
